/**
 * #10426-a1 (MST-6431) — persistent "Shared — not bit-perfect" notice for a bit-perfect zone.
 *
 * Shown whenever playback on a local-native (USB DAC) zone is in shared mode:
 *  - the web fell back to shared (exclusive timeout, format, device busy), or
 *  - exclusive_play resolved with { output_mode: 'shared' } (Connector ≥ v11.0.328).
 * Cleared when a later exclusive play succeeds bit-perfect (or with no output_mode — Connectors
 * ≤ v11.0.327 only resolve on a real exclusive open) or when the active zone changes.
 * Rendered by SharedModeNoticeBadge (via SignalPathBadge) — never only a toast or a log line.
 */
import { create } from 'zustand';
import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from './diagnosticLogger';
import { shouldRouteStickyShared } from './stickySharedFallback';

export type SharedModeReason = 'exclusive_timeout' | 'format' | 'device_busy' | 'connector_shared';
export type ExclusiveOutputMode = 'bit_perfect' | 'shared';

export interface SharedModeNotice {
  zoneId: string | null;
  reason: SharedModeReason;
  /** Connector-provided explanation (e.g. "bit-perfect unavailable on this macOS — playing in shared mode"). */
  message: string | null;
  device: string | null;
}

export interface ExclusivePlayResult {
  output_mode: ExclusiveOutputMode | null;
  reason: string | null;
  message: string | null;
}

export const useSharedModeNoticeStore = create<{ notice: SharedModeNotice | null }>(() => ({ notice: null }));

let lastDispatchOutputMode: ExclusiveOutputMode | null = null;
let zoneWatchUnsub: (() => void) | null = null;

function activeZoneId(): string | null {
  return (useMaestroStore.getState().discovery?.activeZone as { id?: string } | null | undefined)?.id ?? null;
}

function watchZoneChanges(): void {
  if (zoneWatchUnsub || typeof (useMaestroStore as { subscribe?: unknown }).subscribe !== 'function') return;
  zoneWatchUnsub = useMaestroStore.subscribe((state) => {
    const notice = useSharedModeNoticeStore.getState().notice;
    const zoneId = (state.discovery?.activeZone as { id?: string } | null | undefined)?.id ?? null;
    if (notice && notice.zoneId !== zoneId) clearSharedModeNotice('zone_changed');
  });
}

export function showSharedModeNotice(
  reason: SharedModeReason,
  opts: { message?: string | null; device?: string | null } = {},
): void {
  const notice: SharedModeNotice = {
    zoneId: activeZoneId(),
    reason,
    message: opts.message ?? null,
    device: opts.device ?? null,
  };
  useSharedModeNoticeStore.setState({ notice });
  diagnosticLog('warn', 'playback', 'shared mode notice shown', {
    reason,
    zoneId: notice.zoneId,
    message: notice.message,
  });
  watchZoneChanges();
}

export function clearSharedModeNotice(cause: string): void {
  if (!useSharedModeNoticeStore.getState().notice) return;
  useSharedModeNoticeStore.setState({ notice: null });
  diagnosticLog('info', 'playback', 'shared mode notice cleared', { cause });
}

/** Connector ≥ v11.0.328 resolves exclusive_play with an object; older builds resolve null/undefined. */
export function parseExclusivePlayResult(raw: unknown): ExclusivePlayResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const mode = r.output_mode === 'bit_perfect' || r.output_mode === 'shared' ? r.output_mode : null;
  return {
    output_mode: mode,
    reason: typeof r.reason === 'string' ? r.reason : null,
    message: typeof r.message === 'string' ? r.message : null,
  };
}

/** Exclusive dispatch start — forget the previous dispatch's reported output mode. */
export function noteExclusiveDispatchStart(): void {
  lastDispatchOutputMode = null;
}

/** exclusive_play resolved: 'shared' shows the notice; 'bit_perfect' or unknown clears it. */
export function applyExclusivePlayResult(raw: unknown, device: string | null): ExclusiveOutputMode | null {
  const result = parseExclusivePlayResult(raw);
  lastDispatchOutputMode = result?.output_mode ?? null;
  if (lastDispatchOutputMode === 'shared') {
    useMaestroStore.getState().setUsbOutputPath('shared');
    showSharedModeNotice('connector_shared', { message: result?.message ?? null, device });
  } else {
    clearSharedModeNotice(lastDispatchOutputMode === 'bit_perfect' ? 'exclusive_bit_perfect' : 'exclusive_ok_mode_unreported');
  }
  return lastDispatchOutputMode;
}

/** QueueController: playUri resolved on the exclusive route — record the path actually used (sticky shared skips exclusive). */
export function commitExclusiveDispatchOutputPath(): void {
  const store = useMaestroStore.getState();
  const sticky = shouldRouteStickyShared(store.discovery?.activeZone as Parameters<typeof shouldRouteStickyShared>[0], store.currentTrack);
  store.setUsbOutputPath(lastDispatchOutputMode === 'shared' || sticky ? 'shared' : 'exclusive');
  store.setUsbOutputFallbackNotice(null);
}

/** True when the notice applies to this zone (local-native only; matches the zone it was raised on). */
export function sharedModeNoticeAppliesToZone(
  notice: SharedModeNotice | null,
  zone: { id?: string; type?: string } | null | undefined,
): boolean {
  if (!notice || !zone || zone.type !== 'local-native') return false;
  return !notice.zoneId || notice.zoneId === zone.id;
}

/** Test-only reset. */
export function __resetSharedModeNoticeForTests(): void {
  useSharedModeNoticeStore.setState({ notice: null });
  lastDispatchOutputMode = null;
  zoneWatchUnsub?.();
  zoneWatchUnsub = null;
}
